import { useMemo, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ThreeSixtyRounded from '@mui/icons-material/ThreeSixtyRounded';
import { fmt } from '@/features/notas/calculo';
import type { DisciplinaDesempenho } from '@/features/notas/types';

/**
 * PBDI · Data Warehouse: a nota do aluno como um cubo OLAP.
 * Fato: nota. Dimensões: prova (P1, P2, P3) × componente (AWS, individual, grupo) × visão (você, média, mediana).
 * Operações da matéria: slice, dice, drill-down, roll-up e pivot, cada uma com a consulta SQL equivalente.
 */

type Dim = 'prova' | 'componente' | 'visao';
const MEMBROS: Record<Dim, string[]> = {
  prova: ['P1', 'P2', 'P3'],
  componente: ['AWS', 'Individual', 'Grupo'],
  visao: ['Você', 'Média', 'Mediana'],
};
const NOME_DIM: Record<Dim, string> = { prova: 'prova', componente: 'componente', visao: 'visão' };
const PESO: Record<string, number> = { AWS: 0.3, Individual: 0.5, Grupo: 0.2 };
const COMP: Record<string, Record<string, string>> = {
  P1: { AWS: 'T1', Individual: 'IND1', Grupo: 'GRU1' },
  P2: { AWS: 'T2', Individual: 'IND2', Grupo: 'GRU2' },
  P3: { AWS: '', Individual: 'IND3', Grupo: '' },
};

type Celula = { prova: string; componente: string; visao: string; nota: number | null };
type Operacao = 'cubo' | 'slice' | 'dice' | 'drill' | 'rollup' | 'pivot';

function montaFatos(d: DisciplinaDesempenho): Celula[] {
  const out: Celula[] = [];
  for (const prova of MEMBROS.prova)
    for (const componente of MEMBROS.componente)
      for (const visao of MEMBROS.visao) {
        const c = COMP[prova]![componente]!;
        const est = c ? d.turmaInfo.componentes[c as keyof typeof d.turmaInfo.componentes] : null;
        const nota = !c
          ? null
          : visao === 'Você'
            ? (d.notas[c as keyof typeof d.notas] ?? null)
            : visao === 'Média'
              ? (est?.media ?? null)
              : (est?.mediana ?? null);
        out.push({ prova, componente, visao, nota: typeof nota === 'number' ? nota : null });
      }
  return out;
}

/** Tabela no estilo do psql. */
function Psql({ cab, linhas }: { cab: string[]; linhas: (string | number | null)[][] }) {
  const txt = linhas.map((l) =>
    l.map((v) => (typeof v === 'number' ? v.toFixed(2) : (v ?? 'NULL'))),
  );
  const larg = cab.map((c, i) => Math.max(c.length, ...txt.map((l) => String(l[i]).length)));
  const linha = (vals: string[]) => ' ' + vals.map((v, i) => v.padEnd(larg[i]!)).join(' | ');
  const sep = '-' + larg.map((w) => '-'.repeat(w)).join('-+-') + '-';
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        borderRadius: 2,
        bgcolor: '#140E26',
        color: '#D4F45E',
        fontSize: 12.5,
        overflowX: 'auto',
      }}
    >
      {[
        linha(cab),
        sep,
        ...txt.map((l) => linha(l.map(String))),
        `(${linhas.length} ${linhas.length === 1 ? 'linha' : 'linhas'})`,
      ].join('\n')}
    </Box>
  );
}

function Sql({ codigo }: { codigo: string }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        borderRadius: 2,
        bgcolor: 'action.hover',
        fontSize: 12.5,
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
      }}
    >
      {codigo}
    </Box>
  );
}

export function CuboOlap({ d }: { d: DisciplinaDesempenho }) {
  const fatos = useMemo(() => montaFatos(d), [d]);
  const [op, setOp] = useState<Operacao>('cubo');
  const [eixos, setEixos] = useState<[Dim, Dim, Dim]>(['componente', 'prova', 'visao']); // x, y, z
  const [sliceDim, setSliceDim] = useState<Dim>('prova');
  const [sliceMembro, setSliceMembro] = useState('P1');
  const [dice, setDice] = useState<Record<Dim, string[]>>({
    prova: ['P1'],
    componente: ['Individual', 'Grupo'],
    visao: ['Você', 'Média'],
  });
  const [drill, setDrill] = useState<'Grupo' | 'AWS'>('Grupo');
  const [rot, setRot] = useState({ x: -22, y: -35 });
  const arraste = useRef<{ x: number; y: number; rx: number; ry: number } | null>(null);
  const [girando, setGirando] = useState(false);

  const ativa = (c: Celula) => {
    if (op === 'slice') return c[sliceDim] === sliceMembro;
    if (op === 'dice') return (Object.keys(dice) as Dim[]).every((k) => dice[k].includes(c[k]));
    if (op === 'drill') return c.prova === 'P1' && c.componente === drill;
    if (op === 'rollup') return c.prova === 'P1';
    return true;
  };

  const S = 64; // passo entre células
  const s = 54; // aresta do cubinho
  const pos = (c: Celula) => {
    const [ex, ey, ez] = eixos;
    const ix = MEMBROS[ex].indexOf(c[ex]);
    const iy = MEMBROS[ey].indexOf(c[ey]);
    const iz = MEMBROS[ez].indexOf(c[ez]);
    return `translate3d(${(ix - 1) * S}px, ${(iy - 1) * S}px, ${(1 - iz) * S}px)`;
  };
  const faces = [
    `translateZ(${s / 2}px)`,
    `rotateY(180deg) translateZ(${s / 2}px)`,
    `rotateY(90deg) translateZ(${s / 2}px)`,
    `rotateY(-90deg) translateZ(${s / 2}px)`,
    `rotateX(90deg) translateZ(${s / 2}px)`,
    `rotateX(-90deg) translateZ(${s / 2}px)`,
  ];

  const onDown = (e: RPointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    arraste.current = { x: e.clientX, y: e.clientY, rx: rot.x, ry: rot.y };
    setGirando(true);
  };
  const onMove = (e: RPointerEvent) => {
    const a = arraste.current;
    if (!a) return;
    setRot({
      x: Math.max(-80, Math.min(80, a.rx - (e.clientY - a.y) * 0.5)),
      y: a.ry + (e.clientX - a.x) * 0.5,
    });
  };

  // consulta e resultado de cada operação
  const voce = (comp: string, prova = 'P1') =>
    fatos.find((f) => f.prova === prova && f.componente === comp && f.visao === 'Você')?.nota ??
    null;
  let sql = '';
  let resultado: { cab: string[]; linhas: (string | number | null)[][] } = { cab: [], linhas: [] };
  if (op === 'cubo' || op === 'pivot') {
    sql = `SELECT p.prova, c.componente, v.visao, f.nota\nFROM fato_nota f\nJOIN dim_prova p USING (id_prova)\nJOIN dim_componente c USING (id_componente)\nJOIN dim_visao v USING (id_visao)\nWHERE f.ra = :seu_ra\nORDER BY 1, 2, 3;`;
    resultado = {
      cab: ['prova', 'componente', 'visao', 'nota'],
      linhas: fatos
        .filter((f) => f.prova === 'P1')
        .map((f) => [f.prova, f.componente, f.visao, f.nota]),
    };
  } else if (op === 'slice') {
    const outras = (['prova', 'componente', 'visao'] as Dim[]).filter((x) => x !== sliceDim);
    sql = `-- SLICE: fixa ${NOME_DIM[sliceDim]} = '${sliceMembro}' e olha o plano que sobra\nSELECT ${outras.join(', ')}, nota\nFROM vw_cubo_nota\nWHERE ra = :seu_ra\n  AND ${sliceDim} = '${sliceMembro}';`;
    resultado = {
      cab: [...outras, 'nota'],
      linhas: fatos.filter(ativa).map((f) => [f[outras[0]!], f[outras[1]!], f.nota]),
    };
  } else if (op === 'dice') {
    const cond = (Object.keys(dice) as Dim[])
      .map((k) => `  AND ${k} IN (${dice[k].map((m) => `'${m}'`).join(', ')})`)
      .join('\n');
    sql = `-- DICE: um subcubo, escolhendo membros de cada dimensão\nSELECT prova, componente, visao, nota\nFROM vw_cubo_nota\nWHERE ra = :seu_ra\n${cond};`;
    resultado = {
      cab: ['prova', 'componente', 'visao', 'nota'],
      linhas: fatos.filter(ativa).map((f) => [f.prova, f.componente, f.visao, f.nota]),
    };
  } else if (op === 'drill') {
    if (drill === 'Grupo') {
      sql = `-- DRILL-DOWN: do total do grupo (P1) para cada item corrigido\nSELECT r.item, r.peso, f.nota_item,\n       r.peso - f.nota_item AS ficou_de_fora\nFROM fato_correcao f\nJOIN dim_requisito r USING (id_requisito)\nWHERE f.grupo = :seu_grupo\nORDER BY r.ordem;`;
      resultado = {
        cab: ['item', 'peso', 'nota_item', 'ficou_de_fora'],
        linhas: d.requisitos.map((r) => [
          r.nome,
          r.peso,
          r.grupo?.nota ?? null,
          r.peso != null && r.grupo?.nota != null ? r.peso - r.grupo.nota : null,
        ]),
      };
    } else {
      const corte = d.corteAws ?? 0.7;
      sql = `-- DRILL-DOWN: da T1 para cada atividade do AWS Academy\nSELECT a.atividade, f.percentual,\n       CASE WHEN f.percentual >= ${corte.toFixed(2)} THEN 10 ELSE 0 END AS vale\nFROM fato_aws f\nJOIN dim_atividade a USING (id_atividade)\nWHERE f.ra = :seu_ra AND a.etapa = 'T1';`;
      resultado = {
        cab: ['atividade', 'percentual', 'vale'],
        linhas: d.itensT1.map((i) => [
          i.nome,
          (i.aluno ?? 0) * 100,
          (i.aluno ?? 0) >= corte - 1e-9 ? 10 : 0,
        ]),
      };
    }
  } else if (op === 'rollup') {
    sql = `-- ROLL-UP: sobe de componente para prova (0,3·AWS + 0,5·individual + 0,2·grupo)\nSELECT COALESCE(componente, 'TODOS') AS componente,\n       SUM(peso * nota) AS pontos\nFROM vw_cubo_nota\nWHERE ra = :seu_ra AND prova = 'P1' AND visao = 'Você'\nGROUP BY ROLLUP (componente);`;
    const linhas = MEMBROS.componente.map(
      (c) => [c, voce(c) === null ? null : PESO[c]! * voce(c)!] as (string | number | null)[],
    );
    const total = linhas.reduce((t, l) => t + ((l[1] as number) ?? 0), 0);
    resultado = { cab: ['componente', 'pontos'], linhas: [...linhas, ['TODOS', total]] };
  }

  const ops: { v: Operacao; rotulo: string; dica: string }[] = [
    {
      v: 'cubo',
      rotulo: 'Cubo',
      dica: 'Todas as células do cubo: a sua nota, a média e a mediana da turma em cada componente e prova.',
    },
    {
      v: 'slice',
      rotulo: 'Slice',
      dica: 'Fixa um membro de uma dimensão e olha o plano que sobra.',
    },
    { v: 'dice', rotulo: 'Dice', dica: 'Escolhe membros de cada dimensão e fica com um subcubo.' },
    {
      v: 'drill',
      rotulo: 'Drill-down',
      dica: 'Desce do total para o detalhe: do grupo para os itens da prova, ou da T1 para as atividades da AWS.',
    },
    {
      v: 'rollup',
      rotulo: 'Roll-up',
      dica: 'Sobe do detalhe para o total: dos componentes para a P1, com a linha TODOS.',
    },
    {
      v: 'pivot',
      rotulo: 'Pivot',
      dica: 'Troca as dimensões de eixo para ver o cubo de outro jeito.',
    },
  ];

  return (
    <Stack spacing={2.5}>
      <Card>
        <CardContent>
          <Typography variant="h6" component="h2">
            O cubo OLAP da sua nota
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sua nota num data warehouse: o fato é a nota, e as dimensões são prova, componente e
            visão (você, média e mediana da turma). Arraste para girar. Quanto mais escura a célula,
            maior a nota; as células vazias são NULL (provas que ainda não aconteceram).
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={op}
            onChange={(_, v) => v && setOp(v)}
            sx={{ flexWrap: 'wrap', mb: 1 }}
          >
            {ops.map((o) => (
              <ToggleButton key={o.v} value={o.v}>
                {o.rotulo}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {ops.find((o) => o.v === op)?.dica}
          </Typography>

          {op === 'slice' && (
            <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
              <TextField
                select
                size="small"
                label="Dimensão"
                value={sliceDim}
                onChange={(e) => {
                  const k = e.target.value as Dim;
                  setSliceDim(k);
                  setSliceMembro(MEMBROS[k][0]!);
                }}
              >
                {(Object.keys(MEMBROS) as Dim[]).map((k) => (
                  <MenuItem key={k} value={k}>
                    {NOME_DIM[k]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Membro"
                value={sliceMembro}
                onChange={(e) => setSliceMembro(e.target.value)}
              >
                {MEMBROS[sliceDim].map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
          {op === 'dice' && (
            <Stack spacing={1} sx={{ mb: 2 }}>
              {(Object.keys(MEMBROS) as Dim[]).map((k) => (
                <Stack
                  key={k}
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <Typography variant="body2" sx={{ width: 96 }}>
                    {NOME_DIM[k]}
                  </Typography>
                  {MEMBROS[k].map((m) => {
                    const on = dice[k].includes(m);
                    return (
                      <Chip
                        key={m}
                        size="small"
                        label={m}
                        color={on ? 'primary' : 'default'}
                        variant={on ? 'filled' : 'outlined'}
                        onClick={() =>
                          setDice((x) => ({
                            ...x,
                            [k]: on ? x[k].filter((y) => y !== m) : [...x[k], m],
                          }))
                        }
                      />
                    );
                  })}
                </Stack>
              ))}
            </Stack>
          )}
          {op === 'drill' && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={drill}
              onChange={(_, v) => v && setDrill(v)}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="Grupo">Grupo → itens da prova</ToggleButton>
              <ToggleButton value="AWS">AWS → atividades da T1</ToggleButton>
            </ToggleButtonGroup>
          )}
          {op === 'pivot' && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ThreeSixtyRounded />}
              onClick={() => setEixos(([x, y, z]) => [y, z, x])}
              sx={{ mb: 2 }}
            >
              Girar as dimensões (agora: {NOME_DIM[eixos[0]]} nas colunas, {NOME_DIM[eixos[1]]} nas
              linhas, {NOME_DIM[eixos[2]]} na profundidade)
            </Button>
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                onPointerDown={onDown}
                onPointerMove={onMove}
                onPointerUp={() => {
                  arraste.current = null;
                  setGirando(false);
                }}
                onPointerCancel={() => {
                  arraste.current = null;
                  setGirando(false);
                }}
                sx={{
                  height: 320,
                  perspective: '900px',
                  display: 'grid',
                  placeItems: 'center',
                  cursor: 'grab',
                  touchAction: 'none',
                  userSelect: 'none',
                  '&:active': { cursor: 'grabbing' },
                }}
                aria-label="Cubo OLAP da sua nota (arraste para girar)"
                role="img"
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: 0,
                    height: 0,
                    transformStyle: 'preserve-3d',
                    transform: `rotateX(${rot.x}deg) rotateY(${rot.y}deg)`,
                    transition: girando ? 'none' : 'transform 120ms',
                  }}
                >
                  {fatos.map((c) => {
                    const on = ativa(c);
                    const v = c.nota;
                    const fundo =
                      v === null
                        ? 'transparent'
                        : `color-mix(in oklab, var(--faisca-palette-primary-main) ${Math.round(18 + v * 8.2)}%, var(--faisca-palette-background-paper))`;
                    const claro = v !== null && v >= 6;
                    return (
                      <Box
                        key={`${c.prova}${c.componente}${c.visao}`}
                        title={`${c.prova} · ${c.componente} · ${c.visao}: ${v === null ? 'NULL' : fmt(v)}`}
                        sx={{
                          position: 'absolute',
                          width: s,
                          height: s,
                          left: -s / 2,
                          top: -s / 2,
                          transformStyle: 'preserve-3d',
                          transform: pos(c),
                          transition: 'transform 500ms, opacity 300ms',
                          opacity: on ? 1 : 0.1,
                        }}
                      >
                        {faces.map((t, i) => (
                          <Box
                            key={i}
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              transform: t,
                              bgcolor: fundo,
                              border: '1px solid',
                              borderColor: v === null ? 'divider' : 'rgba(255,255,255,0.35)',
                              borderStyle: v === null ? 'dashed' : 'solid',
                              display: 'grid',
                              placeItems: 'center',
                              fontSize: 12,
                              fontWeight: 700,
                              color: claro ? '#fff' : 'text.primary',
                              backfaceVisibility: 'visible',
                            }}
                          >
                            {v === null ? 'NULL' : fmt(v, 1)}
                          </Box>
                        ))}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                component="p"
                sx={{ textAlign: 'center' }}
              >
                colunas: {NOME_DIM[eixos[0]]} ({MEMBROS[eixos[0]].join(', ')}) · linhas:{' '}
                {NOME_DIM[eixos[1]]} ({MEMBROS[eixos[1]].join(', ')}) · profundidade:{' '}
                {NOME_DIM[eixos[2]]} ({MEMBROS[eixos[2]].join(', ')})
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">A consulta</Typography>
                <Sql codigo={sql} />
                <Typography variant="subtitle2">O resultado (como no psql)</Typography>
                <Psql cab={resultado.cab} linhas={resultado.linhas} />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Stack>
  );
}
